import { Buildings, Radio, UsersThree, X } from "@phosphor-icons/react";
import { useIsTabletOrBelow } from "@/hooks/useMediaQuery";

/**
 * MobileFabBar — floating action buttons shown on phones and tablets (i.e.,
 * any viewport <1024px wide). They open the three side panels that we hide
 * on small screens (Fleet, Radio, Mutual Aid) as full-screen drawers.
 *
 * Visible to: phones (landscape only — `RotatePhoneOverlay` blocks portrait)
 *             AND tablets (both orientations).
 * Hidden on : desktop (≥1024px) — the panels are already visible there.
 */
export default function MobileFabBar({
  onOpenFleet,
  onOpenRadio,
  onOpenAid,
  hasAlliance,
  fleetActive,
  radioActive,
  aidActive,
}) {
  const isSmallScreen = useIsTabletOrBelow();
  if (!isSmallScreen) return null;
  // Hide when any panel is already open — looks weird floating on top of itself.
  if (fleetActive || radioActive || aidActive) return null;
  return (
    <div
      data-testid="mobile-fab-bar"
      className="fixed right-3 bottom-3 z-40 flex flex-col gap-2 items-end pointer-events-none"
    >
      <button
        type="button"
        data-testid="mobile-fab-fleet"
        onClick={onOpenFleet}
        className="pointer-events-auto w-11 h-11 flex items-center justify-center border border-emerald-400/40 bg-[#0F1115]/95 backdrop-blur text-emerald-300 hover:bg-emerald-500/15 transition-colors shadow-lg"
        aria-label="Stations & Fleet"
      >
        <Buildings size={18} weight="bold" />
      </button>
      <button
        type="button"
        data-testid="mobile-fab-radio"
        onClick={onOpenRadio}
        className="pointer-events-auto w-11 h-11 flex items-center justify-center border border-amber-400/40 bg-[#0F1115]/95 backdrop-blur text-amber-300 hover:bg-amber-500/15 transition-colors shadow-lg"
        aria-label="Radio Log"
      >
        <Radio size={18} weight="bold" />
      </button>
      {hasAlliance && (
        <button
          type="button"
          data-testid="mobile-fab-aid"
          onClick={onOpenAid}
          className="pointer-events-auto w-11 h-11 flex items-center justify-center border border-orange-400/40 bg-[#0F1115]/95 backdrop-blur text-orange-300 hover:bg-orange-500/15 transition-colors shadow-lg"
          aria-label="Mutual Aid"
        >
          <UsersThree size={18} weight="bold" />
        </button>
      )}
    </div>
  );
}

/**
 * MobilePanelDrawer — fullscreen sheet that wraps the existing panel
 * components on small screens. On desktop the panels render inline as
 * before; on small screens they switch into this drawer.
 */
export function MobilePanelDrawer({ open, onClose, title, children, testId }) {
  if (!open) return null;
  return (
    <div
      data-testid={testId || "mobile-drawer"}
      className="fixed inset-0 z-[60] bg-[#0F1115] flex flex-col"
      // Prevent map scroll-zoom from leaking through.
      style={{ touchAction: "pan-y" }}
    >
      <header className="flex items-center justify-between px-4 h-[52px] border-b border-white/10 bg-[#0F1115] shrink-0">
        <div className="mono text-[11px] uppercase tracking-widest text-zinc-300">
          {title}
        </div>
        <button
          type="button"
          data-testid="mobile-drawer-close"
          onClick={onClose}
          className="w-9 h-9 flex items-center justify-center hover:bg-white/5 text-zinc-300"
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </header>
      <div className="flex-1 min-h-0 overflow-hidden relative">{children}</div>
    </div>
  );
}
